/**
 * @fileoverview Individual navigation item component with support for icons,
 * nested children, and selection states.
 */

import * as React from 'react';
import { styled, SxProps, type Theme } from '@mui/material';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import type {} from '@mui/material/themeCssVarsAugmentation';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import invariant from 'invariant';
import type { Navigation, NavigationPageItem } from '../../types';
import { Link } from '../../components/Link';
import { NavigationContext } from '../../contexts';
import { getItemPath, getItemTitle } from '../../utils/navigationHelpers';
import { SidebarPageItemContext } from './context';

const NavigationListItemButton = styled(ListItemButton)(({ theme }) => ({
  borderRadius: 8,
  '&.Mui-selected': {
    '& .MuiListItemIcon-root': {
      color: (theme.vars ?? theme).palette.primary.dark,
    },
    '& .MuiTypography-root': {
      color: (theme.vars ?? theme).palette.primary.dark,
    },
    '& .MuiSvgIcon-root': {
      color: (theme.vars ?? theme).palette.primary.dark,
    },
    '& .MuiAvatar-root': {
      backgroundColor: (theme.vars ?? theme).palette.primary.dark,
    },
    '& .MuiTouchRipple-child': {
      backgroundColor: (theme.vars ?? theme).palette.primary.dark,
    },
  },
  '& .MuiSvgIcon-root': {
    color: (theme.vars ?? theme).palette.action.active,
  },
  '& .MuiAvatar-root': {
    backgroundColor: (theme.vars ?? theme).palette.action.active,
  },
}));

export interface SidebarPageItemProps {
  /**
   * Navigation page item definition.
   */
  item: NavigationPageItem;
  /**
   * Link `href` for when the item is rendered as a link.
   * @default getItemPath(navigationContext, item)
   */
  href?: string;
  /**
   * The component used to render the item as a link.
   * @default Link
   */
  LinkComponent?: React.ElementType;
  /**
   * If `true`, expands any nested navigation in the item, otherwise collapse it.
   * @default false
   */
  expanded?: boolean;
  /**
   * Use to apply selected styling.
   * @default false
   */
  selected?: boolean;
  /**
   * If `true`, the item is disabled.
   * @default false
   */
  disabled?: boolean;
}

export interface SidebarPageItemContextProps
  extends Partial<SidebarPageItemProps> {
  id: string;
  onClick: (itemId: string, item: NavigationPageItem) => void;
  isSidebarFullyExpanded?: boolean;
  renderNestedNavigation: (subNavigation: Navigation) => React.ReactNode;
}

const LIST_ITEM_ICON_SIZE = 34;

function SidebarPageItem(props: SidebarPageItemProps): React.JSX.Element {
  const navigationContext = React.useContext(NavigationContext);
  const pageItemContextProps = React.useContext(SidebarPageItemContext);

  invariant(pageItemContextProps, 'No navigation page item context provided.');

  const contextAwareProps = {
    ...pageItemContextProps,
    ...props,
  };

  const {
    item,
    href = getItemPath(navigationContext, item),
    LinkComponent: LinkComponentProp,
    expanded = false,
    selected = false,
    disabled = false,
    id,
    onClick,
    isSidebarFullyExpanded = true,
    renderNestedNavigation,
  } = contextAwareProps;

  const handleClick = React.useCallback(() => {
    onClick(id, item);
  }, [id, item, onClick]);

  let nestedNavigationCollapseSx: SxProps<Theme> = { display: 'none' };
  if (isSidebarFullyExpanded) {
    nestedNavigationCollapseSx = {
      ml: 0.5,
      transform: `rotate(${expanded ? 0 : -90}deg)`,
      transition: (theme: Theme) =>
        theme.transitions.create('transform', {
          easing: theme.transitions.easing.sharp,
          duration: 100,
        }),
    };
  }

  const hasExternalHref = href.startsWith('http://') || href.startsWith('https://');

  const LinkComponent = LinkComponentProp ?? (hasExternalHref ? 'a' : Link);

  const title = getItemTitle(item);

  const listItem = (
    <ListItem
      sx={{
        py: 0,
        px: 1,
        overflowX: 'hidden',
      }}
    >
      <NavigationListItemButton
        selected={selected}
        disabled={disabled}
        sx={{
          px: 1,
          height: 32,
        }}
        {...(item.children
          ? {
              onClick: handleClick,
            }
          : {})}
        {...(!item.children
          ? {
              LinkComponent,
              ...(hasExternalHref
                ? {
                    target: '_blank',
                    rel: 'noopener noreferrer',
                  }
                : {}),
              href,
              onClick: handleClick,
            }
          : {})}
      >
        {item.icon ? (
          <Box>
            <ListItemIcon
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: LIST_ITEM_ICON_SIZE,
                '& .MuiSvgIcon-root': {
                  fontSize: '1.3rem',
                },
              }}
            >
              {item.icon}
            </ListItemIcon>
          </Box>
        ) : null}
        <ListItemText
          primary={title}
          sx={{
            ml: 0.3,
            whiteSpace: 'nowrap',
            zIndex: 1,
            '& .MuiTypography-root': {
              fontSize: '0.875rem',
            },
          }}
        />
        {item.action && isSidebarFullyExpanded ? item.action : null}
        {item.children ? <ExpandMoreIcon sx={nestedNavigationCollapseSx} /> : null}
      </NavigationListItemButton>
    </ListItem>
  );

  return (
    <React.Fragment key={id}>
      {listItem}
      {item.children ? (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          {renderNestedNavigation(item.children)}
        </Collapse>
      ) : null}
    </React.Fragment>
  );
}

export { SidebarPageItem };
